import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  X,
  Package,
  Calendar,
  DollarSign,
  Truck,
  MapPin,
  User,
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  Search,
  Filter,
  Loader2,
  ShoppingBag,
  ChevronDown,
  SortAsc,
  SortDesc,
  Copy,
  Sparkles,
  ExternalLink,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Separator } from "./ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Pagination } from "./ui/pagination";
import OrderSort from "./OrderSort";
import { formatCurrency, formatDate, getInitials } from "../lib/utils";
import { Customer, Order, OrderStatus } from "../types";
import { useCustomerOrders } from "../hooks/useCustomerOrders";
import { toast } from "sonner";

interface CustomerOrdersModalProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
  onViewOrderDetails?: (order: Order) => void;
}

export function CustomerOrdersModal({
  customer,
  isOpen,
  onClose,
  onViewOrderDetails,
}: CustomerOrdersModalProps) {
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  const {
    orders,
    loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    searchQuery,
    selectedStatuses,
    selectedPaymentStatuses,
    sortField,
    sortOrder,
    isSearchMode,
    isFilterMode,
    isSortMode,
    isCombinedMode,
    searchLoading,
    filterLoading,
    sortLoading,
    summaryStats,
    setSelectedStatuses,
    setSelectedPaymentStatuses,
    setSorting,
    handleSearch,
    fetchPage,
    refresh,
  } = useCustomerOrders({
    customerId: customer?.id || "",
    pageSize: 8, // Smaller page size for modal
    enabled: isOpen && !!customer,
  });

  const getStatusConfig = (status: OrderStatus) => {
    switch (status) {
      case "pending":
        return {
          color: "bg-yellow-100 text-yellow-800",
          icon: Clock,
          label: "Pending",
          hoverColor: "hover:bg-yellow-100 hover:text-yellow-800",
        };
      case "paid":
        return {
          color: "bg-blue-100 text-blue-800",
          icon: Package,
          label: "Paid",
          hoverColor: "hover:bg-blue-100 hover:text-blue-800",
        };
      case "shipped":
        return {
          color: "bg-purple-100 text-purple-800",
          icon: Truck,
          label: "Shipped",
          hoverColor: "hover:bg-purple-100 hover:text-purple-800",
        };
      case "delivered":
        return {
          color: "bg-green-100 text-green-800",
          icon: CheckCircle,
          label: "Delivered",
          hoverColor: "hover:bg-green-100 hover:text-green-800",
        };
      case "cancelled":
        return {
          color: "bg-red-100 text-red-800",
          icon: AlertCircle,
          label: "Cancelled",
          hoverColor: "hover:bg-red-100 hover:text-red-800",
        };
      case "payment_failed":
        return {
          color: "bg-red-100 text-red-800",
          icon: AlertCircle,
          label: "Payment Failed",
          hoverColor: "hover:bg-red-100 hover:text-red-800",
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800",
          icon: Clock,
          label: "Unknown",
          hoverColor: "hover:bg-gray-100 hover:text-gray-800",
        };
    }
  };

  const getPaymentStatusConfig = (status: string) => {
    switch (status) {
      case "succeeded":
        return {
          color: "bg-green-100 text-green-800",
          label: "Succeeded",
          hoverColor: "hover:bg-green-100 hover:text-green-800",
        };
      case "pending":
        return {
          color: "bg-yellow-100 text-yellow-800",
          label: "Pending",
          hoverColor: "hover:bg-yellow-100 hover:text-yellow-800",
        };
      case "failed":
        return {
          color: "bg-red-100 text-red-800",
          label: "Failed",
          hoverColor: "hover:bg-red-100 hover:text-red-800",
        };
      case "refund_pending":
        return {
          color: "bg-orange-100 text-orange-800",
          label: "Refund Pending",
          hoverColor: "hover:bg-orange-100 hover:text-orange-800",
        };
      case "refunded":
        return {
          color: "bg-purple-100 text-purple-800",
          label: "Refunded",
          hoverColor: "hover:bg-purple-100 hover:text-purple-800",
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800",
          label: "Unknown",
          hoverColor: "hover:bg-gray-100 hover:text-gray-800",
        };
    }
  };

  const copyOrderNumber = (orderNumber: string) => {
    navigator.clipboard.writeText(orderNumber);
    toast.success("Order number copied to clipboard");
  };

  const handleGoToOrder = (order: any) => {
    const orderIdentifier = order.orderNumber || order.id;
    navigate(`/orders?search=${encodeURIComponent(orderIdentifier)}`);
  };

  // Calculate summary statistics for all customer orders (not just current page)
  const totalOrders = summaryStats.totalOrders;
  const totalSpent = summaryStats.totalSpent;
  const averageOrderValue = summaryStats.averageOrderValue;

  // Define filter options
  const orderStatusFilters = [
    {
      key: "pending",
      label: "Pending",
      color: "bg-yellow-100 text-yellow-800",
      hoverColor: "hover:bg-yellow-100 hover:text-yellow-800",
    },
    {
      key: "paid",
      label: "Paid",
      color: "bg-blue-100 text-blue-800",
      hoverColor: "hover:bg-blue-100 hover:text-blue-800",
    },
    {
      key: "shipped",
      label: "Shipped",
      color: "bg-purple-100 text-purple-800",
      hoverColor: "hover:bg-purple-100 hover:text-purple-800",
    },
    {
      key: "delivered",
      label: "Delivered",
      color: "bg-green-100 text-green-800",
      hoverColor: "hover:bg-green-100 hover:text-green-800",
    },
    {
      key: "cancelled",
      label: "Cancelled",
      color: "bg-red-100 text-red-800",
      hoverColor: "hover:bg-red-100 hover:text-red-800",
    },
  ];

  const paymentStatusFilters = [
    {
      key: "pending",
      label: "Pending",
      color: "bg-yellow-100 text-yellow-800",
      hoverColor: "hover:bg-yellow-100 hover:text-yellow-800",
    },
    {
      key: "succeeded",
      label: "Succeeded",
      color: "bg-green-100 text-green-800",
      hoverColor: "hover:bg-green-100 hover:text-green-800",
    },
    {
      key: "failed",
      label: "Failed",
      color: "bg-red-100 text-red-800",
      hoverColor: "hover:bg-red-100 hover:text-red-800",
    },
    {
      key: "refund_pending",
      label: "Refund Pending",
      color: "bg-orange-100 text-orange-800",
      hoverColor: "hover:bg-orange-100 hover:text-orange-800",
    },
    {
      key: "refunded",
      label: "Refunded",
      color: "bg-purple-100 text-purple-800",
      hoverColor: "hover:bg-purple-100 hover:text-purple-800",
    },
  ];

  if (!customer) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
          style={{
            margin: 0,
            padding: "1rem",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{
              type: "spring",
              duration: 0.5,
              bounce: 0.3,
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-purple-600 to-indigo-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <ShoppingBag className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-xl font-bold text-white"
                    >
                      {customer.name}'s Orders
                    </motion.h2>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="flex items-center gap-2 text-white/80 text-sm mt-1"
                    >
                      <span>View all orders for this customer</span>
                      {isCombinedMode && (
                        <Badge
                          variant="outline"
                          className="bg-white/20 text-white border-white/30 hover:bg-white/30"
                        >
                          Combined Mode
                        </Badge>
                      )}
                      {isSearchMode && !isCombinedMode && (
                        <Badge
                          variant="outline"
                          className="bg-white/20 text-white border-white/30 hover:bg-white/30"
                        >
                          Search Mode
                        </Badge>
                      )}
                      {isFilterMode && !isCombinedMode && !isSortMode && (
                        <Badge
                          variant="outline"
                          className="bg-white/20 text-white border-white/30 hover:bg-white/30"
                        >
                          Filter Mode
                        </Badge>
                      )}
                      {isSortMode && !isSearchMode && !isFilterMode && (
                        <Badge
                          variant="outline"
                          className="bg-white/20 text-white border-white/30 hover:bg-white/30"
                        >
                          Sort Mode
                        </Badge>
                      )}
                    </motion.div>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Enhanced Content */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
              {/* Summary Cards */}
              <div className="p-6 border-b border-gray-200 bg-gray-50/50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Card className="border border-gray-200 shadow-sm rounded-xl">
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <ShoppingBag className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-600">
                            Total Orders
                          </p>
                          <p className="text-xl font-bold text-gray-900">
                            {summaryStats?.totalOrders || totalCount}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <Card className="border border-gray-200 shadow-sm rounded-xl">
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <DollarSign className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-600">
                            Total Spent
                          </p>
                          <p className="text-xl font-bold text-green-600">
                            {formatCurrency(summaryStats?.totalSpent || 0)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <Card className="border border-gray-200 shadow-sm rounded-xl">
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Package className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-600">
                            Average Order
                          </p>
                          <p className="text-xl font-bold text-purple-600">
                            {formatCurrency(
                              summaryStats?.averageOrderValue || 0
                            )}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>
              </div>

              {/* Search and Filter Controls */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row gap-3 items-start">
                  {/* Search */}
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search orders, products..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="pl-10 rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20"
                      />
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="flex gap-2 items-center">
                    {/* Sort */}
                    <OrderSort
                      sortField={sortField}
                      sortOrder={sortOrder}
                      onSortChange={setSorting}
                      className="whitespace-nowrap"
                    />

                    {/* Filter Toggle */}
                    <Button
                      variant="outline"
                      onClick={() => setShowFilters(!showFilters)}
                      className="flex items-center gap-2 rounded-xl"
                    >
                      <Filter className="w-4 h-4" />
                      Filters
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${
                          showFilters ? "rotate-180" : ""
                        }`}
                      />
                      {selectedStatuses.length +
                        selectedPaymentStatuses.length >
                        0 && (
                        <Badge
                          variant="secondary"
                          className="ml-1 h-5 min-w-[20px] px-1.5 text-xs hover:bg-gray-200"
                        >
                          {selectedStatuses.length +
                            selectedPaymentStatuses.length}
                        </Badge>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Expanded Filters */}
                {showFilters && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-4 pt-4 border-t border-gray-200"
                  >
                    {/* Order Status Filters */}
                    <div className="flex flex-wrap gap-2 items-center mb-4">
                      <span className="text-sm font-medium text-gray-700 mr-2">
                        Order Status:
                      </span>
                      {orderStatusFilters.map((status) => (
                        <Button
                          key={status.key}
                          variant={
                            selectedStatuses.includes(status.key as OrderStatus)
                              ? "default"
                              : "outline"
                          }
                          size="sm"
                          onClick={() => {
                            if (
                              selectedStatuses.includes(
                                status.key as OrderStatus
                              )
                            ) {
                              setSelectedStatuses(
                                selectedStatuses.filter((s) => s !== status.key)
                              );
                            } else {
                              setSelectedStatuses([
                                ...selectedStatuses,
                                status.key as OrderStatus,
                              ]);
                            }
                          }}
                          className={
                            selectedStatuses.includes(status.key as OrderStatus)
                              ? `${status.color} ${status.hoverColor}`
                              : "hover:bg-gray-50"
                          }
                        >
                          {status.label}
                        </Button>
                      ))}
                    </div>

                    {/* Payment Status Filters */}
                    <div className="flex flex-wrap gap-2 items-center">
                      <span className="text-sm font-medium text-gray-700 mr-2">
                        Payment Status:
                      </span>
                      {paymentStatusFilters.map((status) => (
                        <Button
                          key={status.key}
                          variant={
                            selectedPaymentStatuses.includes(status.key)
                              ? "default"
                              : "outline"
                          }
                          size="sm"
                          onClick={() => {
                            if (selectedPaymentStatuses.includes(status.key)) {
                              setSelectedPaymentStatuses(
                                selectedPaymentStatuses.filter(
                                  (s) => s !== status.key
                                )
                              );
                            } else {
                              setSelectedPaymentStatuses([
                                ...selectedPaymentStatuses,
                                status.key,
                              ]);
                            }
                          }}
                          className={
                            selectedPaymentStatuses.includes(status.key)
                              ? `${status.color} ${status.hoverColor}`
                              : "hover:bg-gray-50"
                          }
                        >
                          {status.label}
                        </Button>
                      ))}
                    </div>

                    {/* Clear Filters */}
                    {(selectedStatuses.length > 0 ||
                      selectedPaymentStatuses.length > 0 ||
                      sortField) && (
                      <div className="pt-2 border-t border-gray-100 mt-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedStatuses([]);
                            setSelectedPaymentStatuses([]);
                            setSorting(null, "desc");
                          }}
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        >
                          Clear All Filters
                        </Button>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>

              {/* Orders Table */}
              <div className="p-6">
                {loading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                      <span className="text-gray-600">Loading orders...</span>
                    </div>
                  </div>
                )}

                {!loading && orders.length === 0 && (
                  <div className="text-center py-12">
                    <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      No orders found
                    </h3>
                    <p className="text-gray-600">
                      This customer hasn't placed any orders yet.
                    </p>
                  </div>
                )}

                {!loading && orders.length > 0 && (
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50/50">
                          <TableHead className="font-semibold">Order</TableHead>
                          <TableHead className="font-semibold">Date</TableHead>
                          <TableHead className="font-semibold">
                            Order Status
                          </TableHead>
                          <TableHead className="font-semibold">
                            Payment Status
                          </TableHead>
                          <TableHead className="font-semibold">Total</TableHead>
                          <TableHead className="font-semibold">
                            Actions
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orders.map((order, index) => {
                          const statusConfig = getStatusConfig(order.status);
                          const paymentStatusConfig = getPaymentStatusConfig(
                            order.payment?.status || "pending"
                          );

                          return (
                            <motion.tr
                              key={order.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.6 + index * 0.05 }}
                              className="hover:bg-gray-50/50 transition-colors"
                            >
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="font-mono text-sm font-medium">
                                    {order.orderNumber || order.id.slice(0, 8)}
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      copyOrderNumber(
                                        order.orderNumber || order.id
                                      )
                                    }
                                    className="p-1 h-auto text-gray-400 hover:text-gray-600"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                </div>
                              </TableCell>

                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm">
                                    {formatDate(order.createdAt)}
                                  </span>
                                </div>
                              </TableCell>

                              <TableCell>
                                <Badge
                                  className={`${statusConfig.color} border rounded-lg`}
                                >
                                  <statusConfig.icon className="w-3 h-3 mr-1" />
                                  {statusConfig.label}
                                </Badge>
                              </TableCell>

                              <TableCell>
                                <Badge
                                  className={`${paymentStatusConfig.color} border rounded-lg`}
                                >
                                  {paymentStatusConfig.label}
                                </Badge>
                              </TableCell>

                              <TableCell>
                                <div className="font-semibold text-gray-900">
                                  {formatCurrency(order.total)}
                                </div>
                              </TableCell>

                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleGoToOrder(order)}
                                    className="hover:bg-purple-50 hover:text-purple-700 rounded-lg"
                                    title="View order details on Orders page"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </Button>
                                  {onViewOrderDetails && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => onViewOrderDetails(order)}
                                      className="hover:bg-purple-50 hover:text-purple-700 rounded-lg"
                                      title="View order details in modal"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </motion.tr>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-gray-200 bg-gray-50/50 flex-shrink-0">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={fetchPage}
                  loading={loading}
                  showInfo={true}
                  totalItems={totalCount}
                  itemsPerPage={8}
                />
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
